import { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { GdsService_OrderAdditionalServiceGdsServiceVip_Fragment } from '@websky/graphql';
import { CombinedWith, Seat, Segment } from './types';
import { GdsServiceById } from '@shared/lib/gdsService';
import { PassengerState } from '../Checkout/store/passengers/reducers';
import { getCombinedNumber, getOppositeDirection, getSeatChangePriceForPassenger, getSeatPrice } from './utils';
import { addServiceAction, removeServiceAction } from '../Checkout/store/selectedServices/actions';
import { getExtraSeatServices } from '../Modules/Checkin/store/order/selectors';
import { findPassengerWithThisSeat } from './components/SeatMap/Seat/utils';
import { useTranslation } from 'react-i18next';
import { SelectedSeatsServiceType } from '../Checkout/store/selectedServices/reducers';
import { PriceDetailsItem } from './components/SeatMap/types';
import { Money as MoneyType } from '../types';
import { subtractMoney } from '@shared/lib/money';

export const useDoubleSeat = (
	activeDoubleSeats: boolean,
	segmentId: string,
	seats: Seat[],
	passengers: PassengerState[],
	onSelect: (seats: Seat[], passengerId: string) => void
) => {
	const [combineWith, setCombineWith] = useState<CombinedWith | null>(null);
	const [prevSeatNumber, setPrevSeatNumber] = useState(null);

	const selectedDoubleSeatsBySegments = useMemo(() => {
		const doubleSeatsBySegments = new Map<string, Map<string, string[]>>();

		passengers.forEach(passenger => {
			if (passenger.seats?.length > 1) {
				passenger.seats.forEach(selectedSeat => {
					const seatsNumbers = passenger.seats
						.filter(seat => seat.segmentId === selectedSeat.segmentId)
						.map(seat => seat.number);

					if (seatsNumbers.length > 1) {
						const existSegment = doubleSeatsBySegments.get(selectedSeat.segmentId);

						if (existSegment) {
							seatsNumbers.map(number => existSegment.set(number, seatsNumbers));
							doubleSeatsBySegments.set(selectedSeat.segmentId, existSegment);
						} else {
							doubleSeatsBySegments.set(
								selectedSeat.segmentId,
								new Map(seatsNumbers.map(number => [number, seatsNumbers]))
							);
						}
					}
				});
			}
		});

		return doubleSeatsBySegments;
	}, [passengers]);

	const onSelectHandler = useCallback(
		(seat: Seat, passengerId: string) => {
			const seatOccupiedBy = findPassengerWithThisSeat(seat, passengers, segmentId),
				seatOccupiedByPassengerId = seatOccupiedBy ? seatOccupiedBy.id : passengerId,
				doubleSeats = selectedDoubleSeatsBySegments?.get(segmentId)?.get(seat.number) ?? [];

			if (seatOccupiedByPassengerId !== passengerId) {
				onSelect(
					doubleSeats?.length ? seats.filter(seat => doubleSeats.includes(seat.number)) : [seat],
					seatOccupiedByPassengerId
				);
			} else if (activeDoubleSeats) {
				const combinedNumber = getCombinedNumber(seat, combineWith ?? seat.combinedWith);
				const combinedSeat = seats.find(seat => seat.number === combinedNumber);

				if (combinedSeat) {
					onSelect([seat, combinedSeat], seatOccupiedByPassengerId);
				} else {
					onSelect([seat], seatOccupiedByPassengerId);
				}
			} else {
				onSelect([seat], seatOccupiedByPassengerId);
			}
		},
		[seats, onSelect, activeDoubleSeats, combineWith, selectedDoubleSeatsBySegments]
	);

	const onMouseEnter = useCallback(
		(seat: Seat, segmentId: string) => {
			if (activeDoubleSeats && seat.combinedWith) {
				const [firstCombineWithNumber, firstCombineWithDirection] = Object.entries(seat.combinedWith)?.[0];

				const doubleSeats = selectedDoubleSeatsBySegments.get(segmentId)?.get(seat.number);
				const prevDirection = seat.combinedWith?.[prevSeatNumber];

				if (doubleSeats?.length) {
					const combinedSeatNumber = doubleSeats?.filter(doubleSeat => doubleSeat !== seat.number)?.[0];
					const combinedDirection = seat.combinedWith?.[combinedSeatNumber];

					setCombineWith({
						[seat.number]: getOppositeDirection(combinedDirection),
						[combinedSeatNumber]: combinedDirection
					});
				} else if (
					prevSeatNumber &&
					seat.number !== prevSeatNumber &&
					prevDirection &&
					!selectedDoubleSeatsBySegments.get(segmentId)?.has(firstCombineWithNumber)
				) {
					setCombineWith({
						[seat.number]: getOppositeDirection(prevDirection),
						[prevSeatNumber]: prevDirection
					});
				} else if (!selectedDoubleSeatsBySegments.get(segmentId)?.has(firstCombineWithNumber)) {
					setCombineWith({
						[seat.number]: getOppositeDirection(firstCombineWithDirection),
						[firstCombineWithNumber]: firstCombineWithDirection
					});
				}

				setPrevSeatNumber(seat.number);
			}
		},
		[activeDoubleSeats, combineWith, selectedDoubleSeatsBySegments]
	);

	const onMouseLeave = useCallback(() => setCombineWith(null), [combineWith]);

	const combinedSeat = useMemo(() => {
		if (combineWith) {
			const firstCombineWithNumber = Object.keys(combineWith)?.[1];

			return seats.find(seat => seat.number === firstCombineWithNumber);
		}

		return null;
	}, [seats, combineWith]);

	return {
		combineWith,
		prevSeatNumber,
		selectedDoubleSeatsBySegments,
		combinedSeat,
		setCombineWith,
		setPrevSeatNumber,
		onSelectHandler,
		onMouseEnter,
		onMouseLeave
	};
};

export const useExtraSeat = (segments: Segment[], passengerId: string, segmentIndex: number) => {
	const dispatch = useDispatch();

	const extraSeatServices = useSelector(getExtraSeatServices);

	const extraSeatService = useMemo(() => {
		let actualService: GdsService_OrderAdditionalServiceGdsServiceVip_Fragment = null;

		const servicesByPassenger = extraSeatServices.filter(service =>
			new Set(service.allowedPassengers.reduce((acc, ids) => [...acc, ...ids], [])).has(passengerId)
		);

		servicesByPassenger.forEach(service => {
			if (service.canBeAdded && (!actualService || service.id > actualService.id)) {
				actualService = service;
			}
		});

		return actualService;
	}, [extraSeatServices, passengerId]);

	const toggleExtraSeatService = useCallback(
		(active: boolean, passengerId: string) => {
			if (active && extraSeatService) {
				dispatch(
					addServiceAction({
						segmentId: segments[0].segmentInfo.id,
						segmentIds: segments.map(segment => segment.segmentInfo.id),
						allowedSegments: extraSeatService.allowedSegments,
						passengerId: passengerId,
						serviceId: extraSeatService.id,
						service: {
							id: extraSeatService.id,
							type: extraSeatService.type
						}
					})
				);
			} else if (!active && extraSeatService) {
				segments.forEach(segment => {
					dispatch(
						removeServiceAction({
							segmentId: segment.segmentInfo.id,
							passengerId: passengerId,
							serviceId: extraSeatService.id,
							service: {
								id: extraSeatService.id,
								type: extraSeatService.type
							},
							count: 0
						})
					);
				});
			}
		},
		[extraSeatService, segments, dispatch]
	);

	const showDoubleSeats = useMemo(() => {
		if (!extraSeatService) {
			return false;
		}

		return (segments ?? []).every(segment =>
			(segment.decks ?? []).some(deck =>
				deck.rows.some(row =>
					row.seats.some((seat: Seat) => seat.combinedWith && Object.keys(seat.combinedWith).length > 0)
				)
			)
		);
	}, [segments, extraSeatService]);

	return {
		showDoubleSeats,
		extraSeatService,
		toggleExtraSeatService
	};
};

export const useSeatPrice = (
	seat: Seat | Partial<SelectedSeatsServiceType>,
	passengerId: string,
	gdsServiceById: GdsServiceById
): { seatPrice: MoneyType; seatPriceDetails: PriceDetailsItem[] | null } => {
	const { t } = useTranslation('SeatMap');

	const { seatPrice, seatPriceDetails } = useMemo(() => {
		const changePrice = getSeatChangePriceForPassenger(seat, gdsServiceById, passengerId);
		const totalSeatPrice = getSeatPrice(seat, passengerId);
		const seatPriceDetails =
			changePrice && changePrice.amount > 0
				? [
						{ label: t('Fee for place'), price: subtractMoney(totalSeatPrice, changePrice) },
						{ label: t('Fee for changing location'), price: changePrice }
				  ]
				: null;

		return {
			seatPrice: totalSeatPrice,
			seatPriceDetails: seatPriceDetails
		};
	}, [seat, passengerId, gdsServiceById]);

	return { seatPrice, seatPriceDetails };
};
